import React from "react";
import { Switch } from "antd";
import { FileTextIcon, ChatCircleIcon } from "@phosphor-icons/react";
import {
  useCustomMutation,
  useInvalidate,
  useTranslate,
} from "@refinedev/core";
import { useProjectInstance } from "pages/media/projects/hooks/useProjectInstance";
import {
  DSection,
  DSectionLabel,
  AudienceRow,
  AudienceRowLeft,
  TEAL_SWITCH_STYLE,
} from "./drawer-styled";

type AudienceViewSectionProps = {
  projectId: string;
  instanceId: string;
  documentEnabled: boolean;
  chatEnabled: boolean;
  onDocumentToggle: (checked: boolean) => void;
  onChatToggle: (checked: boolean) => void;
  onSettingsChange?: () => void;
  isPromptProject?: boolean;
};

export const AudienceViewSection: React.FC<AudienceViewSectionProps> = ({
  projectId,
  instanceId,
  documentEnabled,
  chatEnabled,
  onDocumentToggle,
  onChatToggle,
  onSettingsChange,
  isPromptProject,
}) => {
  const t = useTranslate();
  const { mutateAsync } = useCustomMutation();
  const invalidate = useInvalidate();
  // BE doesn't mark the publication OutOfDate for a /settings PATCH (unlike
  // asset replacement) — see markAsOutOfDate's own no-op guard for why this
  // is safe to call unconditionally.
  const { markAsOutOfDate } = useProjectInstance({ projectId, instanceId });

  const updateSetting = async (
    field: string,
    value: string | boolean | null
  ) => {
    await mutateAsync({
      url: `media/projects/${projectId}/instances/${instanceId}/settings`,
      method: "patch",
      values: { [field]: value },
      successNotification: false,
      errorNotification: false,
    });
    await invalidate({
      resource: `media/projects/${projectId}/instances`,
      id: instanceId,
      invalidates: ["detail"],
    });
    await markAsOutOfDate().catch(() => undefined);
    onSettingsChange?.();
  };

  const handleDocumentToggle = (checked: boolean) => {
    onDocumentToggle(checked);
    void updateSetting("include_document_animations", checked);
  };

  const handleChatToggle = (checked: boolean) => {
    onChatToggle(checked);
    void updateSetting("chat_mode", checked ? "FullChat" : "Disabled");
  };

  return (
    <DSection>
      <DSectionLabel>
        {t(
          "media.components.ProjectSettingsDrawer.audienceView",
          "Audience view"
        )}
      </DSectionLabel>
      <p style={{ fontSize: 12, color: "#9CA3AF", margin: "0 0 12px" }}>
        {t(
          "media.components.ProjectSettingsDrawer.audienceViewDescription",
          "Choose what viewers see when you share this experience."
        )}
      </p>
      {!isPromptProject && (
        <AudienceRow>
          <AudienceRowLeft>
            <FileTextIcon size={16} color="#374151" />
            {t("media.components.ProjectSettingsDrawer.document", "Document")}
          </AudienceRowLeft>
          <Switch
            size="small"
            checked={documentEnabled}
            onChange={handleDocumentToggle}
            style={TEAL_SWITCH_STYLE}
          />
        </AudienceRow>
      )}
      <AudienceRow>
        <AudienceRowLeft>
          <ChatCircleIcon size={16} color="#374151" />
          {t("media.components.ProjectSettingsDrawer.chat", "Chat")}
        </AudienceRowLeft>
        <Switch
          size="small"
          checked={chatEnabled}
          onChange={handleChatToggle}
          style={TEAL_SWITCH_STYLE}
        />
      </AudienceRow>
    </DSection>
  );
};
